'use client'

import * as countryCodes from 'country-codes-list'
import * as Flags from 'country-flag-icons/react/1x1'
import { FlagComponent } from 'country-flag-icons/react/1x1'
import { ChevronDown } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { IOption } from '@/typings/option'
import { cn } from '@/utils/styles/cn'

interface CountryCodeSelectProps {
    value: IOption<string>
    onChange: (value: IOption<string>) => void
    options: IOption<string>[]
    error?: string
    label?: string
    placeholder?: string
}

const COUNTRY_CODES = countryCodes.customList('countryCode', '{countryCode}')

const COUNTRY_CODES_WITH_CALLING_CODE = countryCodes.customList(
    'countryCode',
    '{countryCode}|{countryCallingCode}'
)

export const COUNTRY_OPTIONS: IOption<string>[] = Object.entries(COUNTRY_CODES_WITH_CALLING_CODE)
    .map(([_code, value]) => {
        const [countryCode, calling] = value.split('|')

        return {
            label: calling,
            value: countryCode,
        }
    })
    .sort((a, b) => Number(a.label) - Number(b.label))

export const COUNTRY_CODE_TO_FLAG_COMPONENT_MAP = Object.entries(COUNTRY_CODES).reduce(
    (acc, [_code, v]) => {
        const [countryCode] = v.split('|')

        // biome-ignore lint/suspicious/noExplicitAny: Flags is a map from country code to FlagComponent
        const CountryFlag = (Flags as any)[countryCode] as FlagComponent | undefined

        if (!CountryFlag) return acc

        return {
            ...acc,
            [countryCode]: CountryFlag,
        }
    },
    {} as Record<string, FlagComponent>
)

export const CountryCodeSelect = ({
    value,
    onChange,
    options,
    label,
    error,
    placeholder,
}: CountryCodeSelectProps) => {
    const [open, setOpen] = useState(false)
    const containerRef = useRef<HTMLDivElement | null>(null)
    const activeRef = useRef<HTMLButtonElement | null>(null)

    const active = options.find((o) => o.value === value.value) ?? options[0]

    useEffect(() => {
        const handleClick = (e: MouseEvent) => {
            if (!containerRef.current) return
            if (!containerRef.current.contains(e.target as Node)) {
                setOpen(false)
            }
        }

        document.addEventListener('mousedown', handleClick)
        return () => document.removeEventListener('mousedown', handleClick)
    }, [])

    useEffect(() => {
        if (open && activeRef.current) {
            activeRef.current.scrollIntoView({ behavior: 'instant', block: 'center' })
        }
    }, [open])

    const ActiveFlag = COUNTRY_CODE_TO_FLAG_COMPONENT_MAP[active.value]

    return (
        <div className="relative w-full" ref={containerRef}>
            <button
                type="button"
                onClick={() => {
                    setOpen((prev) => !prev)
                }}
                className={cn(
                    'flex w-full flex-col justify-center rounded-m bg-[#F3F3F3] px-4',
                    label ? 'h-16 gap-1' : 'h-12',
                    'focus:outline-hidden focus-visible:ring-2 focus-visible:ring-black'
                )}
            >
                {label && (
                    <span className="text-left text-[12px] font-medium leading-none text-black/50">
                        {label}
                    </span>
                )}

                <div className="flex w-full items-center justify-between">
                    <div className="flex items-center gap-2">
                        {active && ActiveFlag ? (
                            <>
                                <ActiveFlag className="block h-5 rounded-full" />

                                <span>{`+${active.label}`}</span>
                            </>
                        ) : (
                            <span className="text-[#828282]">
                                {placeholder ?? 'Select a country'}
                            </span>
                        )}
                    </div>

                    <ChevronDown size={18} className="text-black" />
                </div>
            </button>

            {open && (
                <div className="absolute left-0 z-(--z-dropdown) mt-2 max-h-64 w-full overflow-y-auto rounded-l bg-white py-1 shadow-custom">
                    {options.map((option) => {
                        const Flag = COUNTRY_CODE_TO_FLAG_COMPONENT_MAP[option.value]

                        if (!Flag) return null

                        return (
                            <button
                                ref={option.value === value.value ? activeRef : undefined}
                                key={option.value}
                                type="button"
                                onClick={() => {
                                    onChange(option)
                                    setOpen(false)
                                }}
                                className={cn(
                                    'flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm hover:bg-black/5 select-none cursor-pointer',
                                    option.value === value.value && 'bg-black/10'
                                )}
                            >
                                <Flag className="block h-5 rounded-full" />
                                <span>{`+${option.label}`}</span>
                            </button>
                        )
                    })}
                </div>
            )}

            {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
        </div>
    )
}
